import React from 'react';
import { motion } from 'framer-motion';
import { Image as ImageIcon, Video, Mic, ShieldCheck, FileSignature, Leaf, Database } from 'lucide-react';
import { useContent } from '../../lib/content';
import { pickLocale } from '../../lib/api';

/**
 * MetricsStrip — V2 (on-prem realistic).
 *
 * Replaces the previous "184,320 assets generated · 94.6% approval rate"
 * fake aggregates. We have no real client base yet — those would count as
 * AI-washing under FTC 2025 guidance.
 *
 * Instead we show what the deployed platform DOES (capabilities) — closing
 * the section with the user's reminder: "y al final del día, hace imágenes
 * y vídeo bonitos."
 */

const CAPS = [
  { Icon: ImageIcon,   es: 'Imagen editorial 2.5K → 4K',           en: 'Editorial image 2.5K → 4K' },
  { Icon: Video,       es: 'Vídeo 5–12s · imagen y vídeo',         en: 'Video 5–12s · image and video' },
  { Icon: Mic,         es: 'Audio generado opcional',              en: 'Optional generated audio' },
  { Icon: ShieldCheck, es: '9 motores de IA con licencia',         en: '9 AI engines · licence logged' },
  { Icon: FileSignature, es: 'Aprobación humana firmada',          en: 'Signed human approval' },
  { Icon: Leaf,        es: 'Libro de Impacto por imagen',          en: 'Impact Book per image' },
  { Icon: Database,    es: 'Biblioteca y catálogo en tu casa',     en: 'Library and catalogue in-house' },
];

export default function MetricsStrip() {
  const { content, locale } = useContent();
  const data = content.metrics_strip || {};
  const t = (k, fb = '') => pickLocale(data[k], locale, fb);

  return (
    <section
      id="metrics"
      data-testid="metrics-strip"
      className="section bg-bg-secondary border-y border-line"
    >
      <div className="container-x">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-12 md:mb-16">
          <div className="lg:col-span-5">
            <p className="text-eyebrow text-acid mb-4">{t('eyebrow', 'MODARIA · LO QUE HACE TU SISTEMA')}</p>
            <h2
              data-testid="metrics-title"
              className="font-display text-3xl md:text-5xl lg:text-6xl tracking-tight leading-[0.98] text-ink"
            >
              {t('title', 'Y al final del día, hace imágenes y vídeos bonitos.')}
            </h2>
          </div>
          <div className="lg:col-span-7 lg:pt-3">
            <p className="text-ink-muted text-base md:text-lg leading-relaxed">
              {t('lead', 'Toda la maquinaria de Cumplimiento, Permisos, Impacto y Verificación de Origen existe por una razón: que la creatividad fluya tranquila. Estas son las capacidades de la plataforma que se instala dentro de tu propia casa.')}
            </p>
          </div>
        </div>

        <div
          className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 divide-x divide-line border-y border-line"
          data-testid="metrics-grid"
        >
          {CAPS.map((c, i) => {
            const Ic = c.Icon;
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.4 }}
                transition={{ duration: 0.5, delay: i * 0.05 }}
                className="px-4 md:px-5 py-7 md:py-8 group"
                data-testid={`metric-${i}`}
              >
                <Ic size={18} className="text-acid mb-4 group-hover:scale-110 transition-transform" />
                <div className="text-ink text-sm md:text-base leading-snug">
                  {locale === 'es' ? c.es : c.en}
                </div>
              </motion.div>
            );
          })}
        </div>

        <div className="mt-8 flex items-center justify-between text-eyebrow text-ink-dim flex-wrap gap-2">
          <span>{t('source_label', 'PLATAFORMA · INSTALACIÓN EN TU CASA · EXCLUSIVA DE TU MARCA')}</span>
          <span>{t('caveat', 'TUS DATOS, TU BASE DE DATOS, TUS SERVIDORES.')}</span>
        </div>
      </div>
    </section>
  );
}
